import React, { useEffect, useState } from 'react';
import { useBousanmSolide } from '../context/BousanmSolideContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Eye, TrendingUp, Users as UsersIcon, Search, Filter, Download } from 'lucide-react';
import { Input } from '../components/ui/input';
import { formatCurrency } from '../../../../lib/utils';

const AgentsPortfolio = () => {
  const { agentsPortfolio, fetchAgentsPortfolio, loading } = useBousanmSolide();
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchAgentsPortfolio({ page, page_size: 20 });
  }, [page, fetchAgentsPortfolio]);

  if (loading) {
    return (
      <div className="flex-1 space-y-3 p-6 pt-4">
        <div className="animate-pulse space-y-3">
          <div className="h-6 w-48 bg-gray-200 rounded"></div>
          <div className="grid gap-3 md:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  const agents = agentsPortfolio?.results?.agents || [];
  const stats = agentsPortfolio?.results?.statistiques_globales || {};

  return (
    <div className="flex-1 space-y-3 p-6 pt-4">
      {/* Header avec actions - Compact */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Portfolio Agents</h2>
          <p className="text-xs text-muted-foreground">
            Gérez vos agents Bousanm Solidè
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1.5">
            <Filter className="h-3 w-3" />
            Filtrer
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5">
            <Download className="h-3 w-3" />
            Exporter
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Rechercher un agent..."
            className="pl-9"
          />
        </div>
      </div>

      {/* Stats Summary Cards - Compact */}
      <div className="grid gap-3 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1.5 pt-4">
            <CardTitle className="text-xs font-medium">Total Agents</CardTitle>
            <UsersIcon className="h-3 w-3 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-xl font-bold">{stats.nombre_agents || 0}</div>
            <p className="text-[10px] text-muted-foreground mt-0.5">Agents actifs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1.5 pt-4">
            <CardTitle className="text-xs font-medium">Total Clients</CardTitle>
            <UsersIcon className="h-3 w-3 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-xl font-bold">{stats.nombre_clients_bs || 0}</div>
            <p className="text-[10px] text-muted-foreground mt-0.5">Clients BS</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1.5 pt-4">
            <CardTitle className="text-xs font-medium">Commission</CardTitle>
            <TrendingUp className="h-3 w-3 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-xl font-bold">
              {formatCurrency(stats.commission_totale_generee || 0)}
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">HTG</p>
          </CardContent>
        </Card>
      </div>

      {/* Agents List */}
      <div className="space-y-3">
        {agents.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <UsersIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">Aucun agent trouvé</p>
              <p className="text-sm text-gray-400 mt-1">Les agents apparaîtront ici une fois ajoutés</p>
            </CardContent>
          </Card>
        ) : (
          agents.map((agentData) => {
            const agentName = agentData.agent?.nom_complet || agentData.agent?.username || 'Agent';
            const agentUsername = agentData.agent?.username || 'unknown';

            return (
              <Card key={agentUsername} className="hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    {/* Agent Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {agentName.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900">
                            {agentName}
                          </h3>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                              @{agentUsername}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Stats Grid - Compact */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2.5">
                        <div>
                          <p className="text-[10px] text-gray-500 mb-0.5">Clients</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {agentData.statistiques?.nombre_clients || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500 mb-0.5">Épargne</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {formatCurrency(agentData.statistiques?.total_epargne_clients || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500 mb-0.5">Commission</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {formatCurrency(agentData.statistiques?.commission_accumulee || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500 mb-0.5">Ponctualité</p>
                          <p className="text-sm font-semibold text-green-600">
                            {agentData.statistiques?.taux_ponctualite?.toFixed(1) || 0}%
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <Link to={`/bousanm-solide/agent/${agentUsername}`}>
                      <Button size="sm" className="gap-1.5 shrink-0">
                        <Eye className="h-3 w-3" />
                        Détails
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AgentsPortfolio;
